import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Box, Divider, InputBase, Menu, MenuItem, Popover, Typography } from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import CollectionsOutlinedIcon from '@mui/icons-material/CollectionsOutlined'
import ArticleIcon from '@mui/icons-material/Article'
import { useDiRef } from '@/common/react/di-hooks'
import { useBehaviorObservable } from '@/common/react/observable-hooks'
import { SearchScope } from '@/generated/graphql'
import { globalCssVars } from '@/core/theme/GlobalCssVars'
import { SearchFilterChip } from './SearchFilterChip'
import type { SearchService } from '../SearchService'
import type { ObjectsService } from '@/objects/services/ObjectsService'
import { useRightPanel } from '@/core/right-panel/RightPanelContext'
import { EditObjectPanelPage } from '@/objects/edit-object-panel/EditObjectPanelPage'

interface Props {
  open: boolean
  onClose: () => void
}

const scopeOptions: { label: string; value: SearchScope }[] = [
  { label: 'All', value: SearchScope.All },
  { label: 'Collections', value: SearchScope.Collections },
  { label: 'Objects', value: SearchScope.Objects },
]

export function SearchPopup({ open, onClose }: Props) {
  const searchService = useDiRef<SearchService>('SearchService')
  const objectsService = useDiRef<ObjectsService>('ObjectsService')
  const results = useBehaviorObservable(searchService.results$)
  const isLoading = useBehaviorObservable(searchService.isLoading$)

  const [query, setQuery] = useState('')
  const [scope, setScope] = useState<SearchScope>(SearchScope.All)
  const [isArchived, setIsArchived] = useState<boolean | null>(null)

  const [scopeAnchor, setScopeAnchor] = useState<Element | null>(null)
  const [isArchivedAnchor, setIsArchivedAnchor] = useState<Element | null>(null)

  const { openPanel } = useRightPanel()
  const navigate = useNavigate()

  function handleResultClick(result: (typeof results)[number]) {
    if (result.object) {
      const obj = result.object
      const found = objectsService.findObjectByIdOfType(obj.id, obj.typeId)
      if (found) {
        openPanel(<EditObjectPanelPage object={found} />)
        onClose()
      }
    } else if (result.collection) {
      void navigate(`/collection?id=${result.collection.id}`)
      onClose()
    }
  }
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 30)
      setQuery('')
      setScope(SearchScope.All)
      setIsArchived(null)
      searchService.clear()
    }
  }, [open])

  function triggerSearch(q: string, s: SearchScope) {
    searchService.search(
      q.trim() ? { query: q, scope: s !== SearchScope.All ? s : undefined } : null,
    )
  }

  function handleQueryChange(value: string) {
    setQuery(value)
    triggerSearch(value, scope)
  }

  function handleScopeChange(s: SearchScope) {
    setScope(s)
    setScopeAnchor(null)
    triggerSearch(query, s)
  }

  const showResults = results.length > 0 || isLoading

  return (
    <Popover
      open={open}
      onClose={onClose}
      anchorReference="anchorPosition"
      anchorPosition={{ top: window.innerHeight * 0.21, left: window.innerWidth / 2 }}
      transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      disableAutoFocus
      slotProps={{
        paper: {
          sx: {
            width: 620,
            maxHeight: 520,
            display: 'flex',
            flexDirection: 'column',
            borderRadius: globalCssVars.radius5,
            border: '1px solid var(--mui-palette-divider)',
          },
        },
      }}
    >
      {/* Search input */}
      <Box sx={{ display: 'flex', alignItems: 'center', px: 1.5, py: 1, gap: 1 }}>
        <SearchIcon sx={{ color: 'text.secondary', fontSize: 20, flexShrink: 0 }} />
        <InputBase
          inputRef={inputRef}
          value={query}
          onChange={(e) => handleQueryChange(e.target.value)}
          placeholder="Search..."
          fullWidth
          sx={{ fontSize: 15 }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') onClose()
          }}
        />
      </Box>

      <Divider />

      {/* Filter row */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 1.5,
          py: 0.75,
          gap: 0.75,
          flexWrap: 'wrap',
        }}
      >
        <SearchFilterChip
          label="Scope"
          valueLabel={scopeOptions.find((o) => o.value === scope)!.label}
          onClick={(e) => setScopeAnchor(e.currentTarget)}
          onClear={scope !== SearchScope.All ? () => handleScopeChange(SearchScope.All) : undefined}
        />

        {/* <SearchFilterChip label="Object Type" valueLabel="Any" onClick={() => {}} /> */}

        {isArchived !== null && (
          <SearchFilterChip
            label="Archived"
            valueLabel={isArchived ? 'Yes' : 'No'}
            onClick={(e) => setIsArchivedAnchor(e.currentTarget)}
            onClear={() => setIsArchived(null)}
          />
        )}
        {/* <IconButton size="small" onClick={(e) => setFilterAnchor(e.currentTarget)} sx={{ ml: 'auto', color: 'text.secondary' }} title="More filters">
          <FilterListIcon sx={{ fontSize: 18 }} />
        </IconButton> */}
      </Box>

      {/* Results */}
      {showResults && (
        <>
          <Divider />
          <Box sx={{ overflowY: 'auto', flex: 1 }}>
            {isLoading && !results.length && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ px: 2, py: 1.5, display: 'block' }}
              >
                Searching…
              </Typography>
            )}
            {results.map((result, i) => {
              const obj = result.object
              const col = result.collection
              const title = obj ? (obj.name ?? '(untitled)') : (col?.name ?? '(untitled)')
              const description = obj?.description ?? null
              return (
                <Box
                  key={i}
                  onClick={() => handleResultClick(result)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    px: 2,
                    py: 0.875,
                    gap: 1.5,
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  {col ? (
                    <CollectionsOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary', flexShrink: 0 }} />
                  ) : (
                    <ArticleIcon sx={{ fontSize: 18, color: 'text.secondary', flexShrink: 0 }} />
                  )}
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" noWrap>
                      {title}
                    </Typography>
                    {description && (
                      <Typography variant="caption" color="text.secondary" noWrap display="block">
                        {description}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )
            })}
          </Box>
        </>
      )}

      {/* Scope options */}
      <Menu anchorEl={scopeAnchor} open={Boolean(scopeAnchor)} onClose={() => setScopeAnchor(null)}>
        {scopeOptions.map(({ label, value }) => (
          <MenuItem
            key={value}
            selected={scope === value}
            onClick={() => handleScopeChange(value)}
            sx={{ fontSize: 14 }}
          >
            {label}
          </MenuItem>
        ))}
      </Menu>

      {/* isArchived chip options */}
      <Menu
        anchorEl={isArchivedAnchor}
        open={Boolean(isArchivedAnchor)}
        onClose={() => setIsArchivedAnchor(null)}
      >
        <MenuItem
          selected={isArchived === true}
          onClick={() => {
            setIsArchived(true)
            setIsArchivedAnchor(null)
          }}
          sx={{ fontSize: 14 }}
        >
          Yes
        </MenuItem>
        <MenuItem
          selected={isArchived === false}
          onClick={() => {
            setIsArchived(false)
            setIsArchivedAnchor(null)
          }}
          sx={{ fontSize: 14 }}
        >
          No
        </MenuItem>
      </Menu>

      {/* Filter Menu (hidden until implemented):
      <Menu anchorEl={filterAnchor} open={Boolean(filterAnchor)} onClose={() => setFilterAnchor(null)} slotProps={{ paper: { sx: { minWidth: 180 } } }}>
        ...
      </Menu> */}
    </Popover>
  )
}
